import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Alert, Skeleton, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, Avatar,
} from '@mui/material';
import {
  QrCode2, Edit, CheckCircle, Cancel, HourglassEmpty,
  ContentCopy, Refresh, FilterList, AccountBalanceWallet,
  VerifiedUser, PendingActions, MoneyOff,
} from '@mui/icons-material';
import { gymUpiAPI, uploadAPI, SERVER_URL } from '../services/api';
import { formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtAmount(n) {
  return formatCurrency(n);
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" sx={{ p: 0.25, ml: 0.25 }}
        onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
        <ContentCopy sx={{ fontSize: 12, color: copied ? '#16a34a' : '#9CA3AF' }} />
      </IconButton>
    </Tooltip>
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────
const STATUS = {
  verified: { label: 'Verified',  icon: <CheckCircle sx={{ fontSize: 13 }} />, bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  pending:  { label: 'Pending',   icon: <HourglassEmpty sx={{ fontSize: 13 }} />, bg: '#fefce8', text: '#ca8a04', border: '#fef08a' },
  rejected: { label: 'Rejected',  icon: <Cancel sx={{ fontSize: 13 }} />, bg: '#fff1f2', text: '#dc2626', border: '#fecdd3' },
};

function StatusChip({ status }) {
  const cfg = STATUS[status] || STATUS.pending;
  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: 11, height: 22 }}
    />
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, bg }) {
  return (
    <Card sx={{ flex: 1, minWidth: 140, border: `1px solid ${bg}` }}>
      <CardContent sx={{ p: '14px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: bg, display: 'flex' }}>{icon}</Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function GymUpiPayments() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const gymId = currentUser?.gymId;

  // ── UPI settings state ────────────────────────────────────────────────────
  const [settings, setSettings]       = useState({ upiId: '', qrImage: '', note: '' });
  const [editMode, setEditMode]       = useState(false);
  const [editForm, setEditForm]       = useState({ upiId: '', qrImage: '', note: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);
  const [settingsAlert, setSettingsAlert] = useState(null);

  // ── Payments state ────────────────────────────────────────────────────────
  const [stats, setStats]             = useState({ verified: 0, pending: 0, rejected: 0, totalVerified: 0 });
  const [payments, setPayments]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [pages, setPages]             = useState(1);
  const [page, setPage]               = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]         = useState(false);
  const [listAlert, setListAlert]     = useState(null);

  // ── Verify / Reject dialog ────────────────────────────────────────────────
  const [actionDialog, setActionDialog] = useState({ open: false, payment: null, type: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // ── Load settings ─────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    if (!gymId) return;
    try {
      const res = await gymUpiAPI.getSettings(gymId);
      setSettings(res.data.data);
      setEditForm(res.data.data);
    } catch (e) {
      console.error('loadSettings', e);
    }
  }, [gymId]);

  // ── Load stats ────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!gymId) return;
    try {
      const res = await gymUpiAPI.getStats(gymId);
      setStats(res.data.data);
    } catch (e) {
      console.error('loadStats', e);
    }
  }, [gymId]);

  // ── Load payments ─────────────────────────────────────────────────────────
  const loadPayments = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    try {
      const res = await gymUpiAPI.listPayments(gymId, { page, limit: 10, status: statusFilter || undefined });
      setPayments(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setListAlert({ type: 'error', msg: 'Failed to load payments' });
    } finally {
      setLoading(false);
    }
  }, [gymId, page, statusFilter]);

  useEffect(() => { loadSettings(); loadStats(); }, [loadSettings, loadStats]);
  useEffect(() => { loadPayments(); }, [loadPayments]);

  // ── Save settings ─────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setSettingsSaving(true);
    setSettingsAlert(null);
    try {
      await gymUpiAPI.upsertSettings(gymId, editForm);
      setSettings(editForm);
      setEditMode(false);
      setSettingsAlert({ type: 'success', msg: 'UPI settings saved.' });
    } catch (e) {
      setSettingsAlert({ type: 'error', msg: e.response?.data?.message || 'Save failed' });
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── QR image upload ───────────────────────────────────────────────────────
  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQrUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      const url = res.data.url || res.data.data?.url || '';
      setEditForm(f => ({ ...f, qrImage: url }));
    } catch {
      setSettingsAlert({ type: 'error', msg: 'QR upload failed' });
    } finally {
      setQrUploading(false);
    }
  };

  // ── Verify / Reject ───────────────────────────────────────────────────────
  const openAction = (payment, type) => {
    setActionDialog({ open: true, payment, type });
    setRejectReason('');
  };

  const confirmAction = async () => {
    const { payment, type } = actionDialog;
    setActionLoading(true);
    try {
      if (type === 'verify') {
        await gymUpiAPI.verifyPayment(payment._id);
      } else {
        await gymUpiAPI.rejectPayment(payment._id, rejectReason);
      }
      setActionDialog({ open: false, payment: null, type: '' });
      await Promise.all([loadStats(), loadPayments()]);
    } catch (e) {
      setListAlert({ type: 'error', msg: e.response?.data?.message || 'Action failed' });
    } finally {
      setActionLoading(false);
    }
  };

  if (!gymId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">No gym associated with your account.</Alert>
      </Box>
    );
  }

  const qrSrc = settings.qrImage
    ? (settings.qrImage.startsWith('http') ? settings.qrImage : `${SERVER_URL}${settings.qrImage}`)
    : null;

  const editQrSrc = editForm.qrImage
    ? (editForm.qrImage.startsWith('http') ? editForm.qrImage : `${SERVER_URL}${editForm.qrImage}`)
    : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <PageHeader
        title="UPI Payments"
        subtitle="Manage your gym's UPI details and verify member payments"
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => { loadStats(); loadPayments(); }}>Refresh</Button>}
      />

      {/* ── UPI Settings card ─────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <QrCode2 sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={700}>UPI Settings</Typography>
          </Stack>
          {!editMode && (
            <Button startIcon={<Edit />} size="small" variant="outlined" onClick={() => { setEditMode(true); setEditForm(settings); setSettingsAlert(null); }}>
              Edit
            </Button>
          )}
        </Stack>

        {settingsAlert && (
          <Alert severity={settingsAlert.type} sx={{ mb: 2 }} onClose={() => setSettingsAlert(null)}>
            {settingsAlert.msg}
          </Alert>
        )}

        {!editMode ? (
          /* ── View mode ─────────────────────────────────────────────────── */
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start">
            {/* QR preview */}
            <Box sx={{ flexShrink: 0 }}>
              {qrSrc ? (
                <Box component="img" src={qrSrc} alt="UPI QR"
                  sx={{ width: 140, height: 140, borderRadius: 2, border: '1px solid', borderColor: 'divider', objectFit: 'contain', bgcolor: 'background.paper' }} />
              ) : (
                <Box sx={{ width: 140, height: 140, borderRadius: 2, border: '2px dashed', borderColor: 'divider',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                  <QrCode2 sx={{ fontSize: 40 }} />
                  <Typography variant="caption">No QR yet</Typography>
                </Box>
              )}
            </Box>
            {/* Details */}
            <Box flex={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                UPI ID
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
                <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 16 }}>
                  {settings.upiId || <span style={{ color: 'text.secondary', fontWeight: 400 }}>Not set</span>}
                </Typography>
                {settings.upiId && <CopyBtn value={settings.upiId} />}
              </Stack>
              {settings.note && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                    Note for members
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{settings.note}</Typography>
                </>
              )}
            </Box>
          </Stack>
        ) : (
          /* ── Edit mode ─────────────────────────────────────────────────── */
          <Stack spacing={2}>
            <TextField
              label="UPI ID"
              value={editForm.upiId}
              onChange={e => setEditForm(f => ({ ...f, upiId: e.target.value }))}
              placeholder="e.g. gymowner@upi"
              size="small"
              fullWidth
              helperText="Members will pay to this UPI ID"
            />
            <TextField
              label="Note for members (optional)"
              value={editForm.note}
              onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Add your name in the UPI note"
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            {/* QR upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} mb={0.5} display="block">
                QR Code Image (optional)
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {editQrSrc && (
                  <Box component="img" src={editQrSrc} alt="QR preview"
                    sx={{ width: 80, height: 80, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', objectFit: 'contain' }} />
                )}
                <Button variant="outlined" component="label" size="small" disabled={qrUploading}>
                  {qrUploading ? 'Uploading…' : editQrSrc ? 'Replace QR' : 'Upload QR'}
                  <input type="file" hidden accept="image/*" onChange={handleQrUpload} />
                </Button>
                {editForm.qrImage && (
                  <Button size="small" color="error" onClick={() => setEditForm(f => ({ ...f, qrImage: '' }))}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => { setEditMode(false); setSettingsAlert(null); }}>Cancel</Button>
              <Button variant="contained" onClick={saveSettings} disabled={settingsSaving}>
                {settingsSaving ? 'Saving…' : 'Save Settings'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <StatCard
          icon={<AccountBalanceWallet sx={{ color: '#16a34a', fontSize: 20 }} />}
          label="Total Received"
          value={fmtAmount(stats.totalVerified)}
          color="#16a34a"
          bg="#f0fdf4"
        />
        <StatCard
          icon={<VerifiedUser sx={{ color: '#2563eb', fontSize: 20 }} />}
          label="Verified"
          value={stats.verified}
          color="#2563eb"
          bg="#eff6ff"
        />
        <StatCard
          icon={<PendingActions sx={{ color: '#ca8a04', fontSize: 20 }} />}
          label="Pending"
          value={stats.pending}
          color="#ca8a04"
          bg="#fefce8"
        />
        <StatCard
          icon={<MoneyOff sx={{ color: '#dc2626', fontSize: 20 }} />}
          label="Rejected"
          value={stats.rejected}
          color="#dc2626"
          bg="#fff1f2"
        />
      </Stack>

      {/* ── Payments list ─────────────────────────────────────────────────── */}
      <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {/* List header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" px={2.5} py={2}
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={700}>
            Payment Records
            {total > 0 && <Typography component="span" variant="body2" color="text.secondary" ml={1}>({total} total)</Typography>}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FilterList sx={{ color: 'text.secondary', fontSize: 18 }} />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                displayEmpty
                renderValue={(selected) => selected === '' ? 'All' : selected}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {listAlert && (
          <Alert severity={listAlert.type} sx={{ m: 2 }} onClose={() => setListAlert(null)}>
            {listAlert.msg}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>UTR Number</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton height={20} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No payments found
                  </TableCell>
                </TableRow>
              ) : (
                payments.map(p => (
                  <TableRow key={p._id} hover>
                    {/* Member */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light', color: 'primary.main' }}>
                          {p.userId?.name?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                            {p.userId?.name || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.userId?.email || '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Amount */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {fmtAmount(p.amount)}
                      </Typography>
                    </TableCell>

                    {/* UTR */}
                    <TableCell>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {p.utrNumber}
                        </Typography>
                        <CopyBtn value={p.utrNumber} />
                      </Stack>
                    </TableCell>

                    {/* Note */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 120, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.note || '—'}
                      </Typography>
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(p.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <StatusChip status={p.status} />
                      {p.status === 'rejected' && p.rejectReason && (
                        <Tooltip title={p.rejectReason}>
                          <Typography variant="caption" color="error" display="block" sx={{ cursor: 'help', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.rejectReason}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      {p.status === 'pending' && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Verify payment">
                            <IconButton size="small" color="success" onClick={() => openAction(p, 'verify')}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject payment">
                            <IconButton size="small" color="error" onClick={() => openAction(p, 'reject')}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                      {p.status !== 'pending' && (
                        <Typography variant="caption" color="text.disabled">
                          {p.verifiedAt ? fmtDate(p.verifiedAt) : '—'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Pagination count={pages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      {/* ── Verify / Reject dialog ─────────────────────────────────────────── */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, payment: null, type: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {actionDialog.type === 'verify' ? '✅ Verify Payment' : '❌ Reject Payment'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.payment && (
            <Box>
              <Typography variant="body2" mb={1}>
                <strong>Member:</strong> {actionDialog.payment.userId?.name} ({actionDialog.payment.userId?.email})
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Amount:</strong> {fmtAmount(actionDialog.payment.amount)}
              </Typography>
              <Typography variant="body2" mb={actionDialog.type === 'reject' ? 2 : 0}>
                <strong>UTR:</strong> {actionDialog.payment.utrNumber}
              </Typography>
              {actionDialog.type === 'reject' && (
                <TextField
                  label="Reason for rejection (optional)"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  placeholder="e.g. UTR not found in bank statement"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionDialog({ open: false, payment: null, type: '' })} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.type === 'verify' ? 'success' : 'error'}
            onClick={confirmAction}
            disabled={actionLoading}
          >
            {actionLoading ? 'Processing…' : actionDialog.type === 'verify' ? 'Confirm Verify' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
